import React, { useState, useCallback } from 'react';
import { Segment, Icon, Input, Button, Label, Table, Message } from 'semantic-ui-react';

/**
 * Derive a "Book Title" suggestion from a filename
 * e.g., "dark-forest-final-v3.pdf" → "Dark Forest Final V3"
 */
const deriveTitle = (fileName) => {
  return fileName
    .replace(/\.[^/.]+$/, '') // remove extension
    .replace(/[-_]+/g, ' ')  // dashes/underscores → spaces
    .replace(/\b\w/g, c => c.toUpperCase()) // title case
    .trim();
};

/**
 * Ensure a filename keeps its original extension
 */
const enforceExtension = (newName, originalExt) => {
  const currentExt = '.' + newName.split('.').pop().toLowerCase();
  if (currentExt === originalExt.toLowerCase()) return newName;
  // Strip any wrong extension and append correct one
  const base = newName.replace(/\.[^/.]+$/, '');
  return base + originalExt;
};

/**
 * Kebab-case a string for file naming
 * e.g., "The Dark Forest" → "the-dark-forest"
 */
const toKebab = (str) => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * FileStagingList — Shows dropped/selected files with editable names before upload.
 *
 * Props:
 *   stagedFiles    — array of { id, file, aliasName, originalName, ext }
 *   onUpdateAlias  — callback(id, newAliasName)
 *   onRemove       — callback(id)
 *   onConfirmUpload — callback(stagedFiles) — user clicks "Upload All"
 *   fileType       — 'pdf' | 'cover' | 'authorImage' — controls display hints
 *   disabled       — boolean
 *   uploading      — boolean — shows loading state on upload button
 *   showTitle      — boolean — show derived title column (for PDFs)
 */
const FileStagingList = ({
  stagedFiles = [],
  onUpdateAlias,
  onRemove,
  onConfirmUpload,
  fileType = 'pdf',
  disabled = false,
  uploading = false,
  showTitle = false,
}) => {
  if (stagedFiles.length === 0) return null;

  const fileIcon = fileType === 'pdf' ? 'file pdf outline' : 'image';
  const fileColor = fileType === 'pdf' ? 'red' : 'blue';

  return (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5em' }}>
        <Label size="medium">
          <Icon name="list" />
          {stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''} staged for upload
        </Label>
        <Button
          primary
          size="small"
          onClick={() => onConfirmUpload && onConfirmUpload(stagedFiles)}
          disabled={disabled || uploading || stagedFiles.length === 0}
          loading={uploading}
        >
          <Icon name="upload" />
          Upload {stagedFiles.length === 1 ? 'File' : `All ${stagedFiles.length} Files`}
        </Button>
      </div>

      <Table compact size="small" basic="very">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}></Table.HeaderCell>
            <Table.HeaderCell width={showTitle ? 5 : 8}>
              File Name (editable)
            </Table.HeaderCell>
            {showTitle && (
              <Table.HeaderCell width={4}>Derived Title</Table.HeaderCell>
            )}
            <Table.HeaderCell width={2}>Size</Table.HeaderCell>
            <Table.HeaderCell width={2}>Original</Table.HeaderCell>
            <Table.HeaderCell width={1}></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {stagedFiles.map(staged => {
            const sizeStr = staged.file
              ? `${(staged.file.size / (1024 * 1024)).toFixed(1)} MB`
              : '—';
            const title = deriveTitle(staged.aliasName);
            const nameChanged = staged.aliasName !== staged.originalName;

            return (
              <Table.Row key={staged.id}>
                <Table.Cell>
                  <Icon name={fileIcon} color={fileColor} />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    fluid
                    size="small"
                    value={staged.aliasName}
                    onChange={(e) => {
                      let val = e.target.value;
                      // Enforce extension
                      val = enforceExtension(val, staged.ext);
                      onUpdateAlias && onUpdateAlias(staged.id, val);
                    }}
                    disabled={disabled}
                    action={nameChanged ? {
                      icon: 'undo',
                      title: 'Reset to original name',
                      onClick: () => onUpdateAlias && onUpdateAlias(staged.id, staged.originalName),
                      size: 'small',
                    } : undefined}
                  />
                  {nameChanged && (
                    <div style={{ fontSize: '0.75em', color: '#888', marginTop: '2px' }}>
                      <Icon name="edit" size="small" /> Renamed from: {staged.originalName}
                    </div>
                  )}
                </Table.Cell>
                {showTitle && (
                  <Table.Cell>
                    <span style={{ color: '#555', fontSize: '0.9em' }}>{title}</span>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <span style={{ fontSize: '0.85em', color: '#888' }}>{sizeStr}</span>
                </Table.Cell>
                <Table.Cell>
                  <span style={{ fontSize: '0.75em', color: '#aaa' }}>
                    {staged.originalName}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Button
                    icon="close"
                    size="mini"
                    basic
                    color="red"
                    onClick={() => onRemove && onRemove(staged.id)}
                    disabled={disabled}
                    title="Remove from staging"
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      <Message info size="mini">
        <Icon name="lightbulb" />
        Edit file names before uploading. The alias name will be used in S3 storage and the database record.
      </Message>
    </Segment>
  );
};

/**
 * Hook: useFileStagingList — manages staged file state with aliasing.
 *
 * Returns: { stagedFiles, addFiles, updateAlias, removeFile, clearStaged }
 */
export const useFileStaging = () => {
  const [stagedFiles, setStagedFiles] = useState([]);

  const addFiles = useCallback((files) => {
    const newEntries = files.map((file, idx) => {
      const ext = '.' + file.name.split('.').pop();
      return {
        id: `${Date.now()}-${idx}`,
        file,
        originalName: file.name,
        aliasName: file.name,
        ext,
      };
    });
    setStagedFiles(prev => [...prev, ...newEntries]);
  }, []);

  const updateAlias = useCallback((id, newName) => {
    setStagedFiles(prev =>
      prev.map(f => f.id === id ? { ...f, aliasName: newName } : f)
    );
  }, []);

  const removeFile = useCallback((id) => {
    setStagedFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const clearStaged = useCallback(() => {
    setStagedFiles([]);
  }, []);

  return { stagedFiles, addFiles, updateAlias, removeFile, clearStaged };
};

// Export utilities for reuse
export { deriveTitle, enforceExtension, toKebab };
export default FileStagingList;
